import { useMemo, lazy, Suspense } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteShell } from "@/components/layout/SiteShell";
import { AdSlot } from "@/components/AdSlot";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { parseISO, todayISO, formatLong, calendarDatePath } from "@/lib/calendar/dates";
import { gregorianToJawa } from "@/lib/calendar/jawa";
import { TERM_ENTITIES } from "@/lib/seo/entities";

// Lazy-loaded: pdf-lib chunk only fetched when user opens the PDF dialog.
const WetonPdfButton = lazy(() =>
  import("@/components/WetonPdfButton").then((m) => ({ default: m.WetonPdfButton })),
);

const WATAK: Record<number, string> = {
  7: "Pendito Kang Lelaku - suka bepergian, pemikir.",
  8: "Lakune Geni - pemarah, ambisius.",
  9: "Lakune Angin - gampang berubah, fleksibel.",
  10: "Pandhita Mbangun Teki - tekun, telaten.",
  11: "Lakune Setan - keras, berkemauan kuat.",
  12: "Lakune Kembang - disukai banyak orang.",
  13: "Lakune Lintang - kesepian tapi cerdas.",
  14: "Lakune Mbulan - penyabar, pengasih.",
  15: "Lakune Geni - pekerja keras.",
  16: "Lakune Bumi - sabar, pemurah.",
  17: "Lakune Gunung - teguh pendirian.",
  18: "Lakune Paripurna - pemimpin alami.",
};

const wetonSearchSchema = z.object({
  // No .default() — that triggers TanStack to redirect bare /tools/cek-weton/
  // to /tools/cek-weton/?tgl=... (307). fallback() alone supplies a value
  // for missing/invalid input without rewriting the URL.
  tgl: fallback(z.string().regex(/^\d{4}-\d{2}-\d{2}$/), todayISO()),
});

export const Route = createFileRoute("/tools/cek-weton")({
  validateSearch: zodValidator(wetonSearchSchema),
  head: () => {
    const url = `https://hari.co.id/tools/cek-weton/`;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Cek Weton Jawa Online",
        applicationCategory: "UtilityApplication",
        operatingSystem: "Any",
        url,
        inLanguage: "id-ID",
        description: "Cek weton, pasaran, neptu, wuku, watak, pancasuda, dan pranata mangsa berdasarkan tanggal lahir.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "IDR" },
        about: [TERM_ENTITIES.weton, TERM_ENTITIES.neptu, TERM_ENTITIES.pasaran].filter(Boolean),
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${url}?tgl={birth_date}`,
          },
          "query-input": "required name=birth_date",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
          { "@type": "ListItem", position: 2, name: "Kalkulator", item: "https://hari.co.id/tools/" },
          { "@type": "ListItem", position: 3, name: "Cek Weton", item: url },
        ],
      },
    ];
    return {
      meta: [
        { title: "Cek Weton Jawa Online: Hari, Pasaran, Neptu, Watak" },
        { name: "description", content: "Cek weton Jawa berdasarkan tanggal lahir: hari, pasaran, neptu, wuku, watak, pancasuda, dan pranata mangsa." },
        { property: "og:title", content: "Cek Weton Jawa Online" },
        { property: "og:description", content: "Hitung weton, pasaran, neptu, dan wuku dari tanggal lahir Anda." },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: ld.map((obj) => ({ type: "application/ld+json", children: JSON.stringify(obj) })),
    };
  },
  component: CekWetonPage,
});

function CekWetonPage() {
  const { tgl } = Route.useSearch();
  const navigate = useNavigate({ from: "/tools/cek-weton" });
  const parsed = parseISO(tgl);
  const result = useMemo(() => {
    if (!parsed) return null;
    return gregorianToJawa(parsed.y, parsed.m, parsed.d);
  }, [parsed]);
  return (
    <SiteShell>
      <header className="py-6">
        <h1 className="text-3xl font-bold tracking-tight">Cek Weton Jawa</h1>
        <p className="mt-1 text-muted-foreground">Masukkan tanggal lahir untuk mengetahui weton, neptu, wuku, dan watak.</p>
      </header>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 sm:p-6">
            <Label htmlFor="tgl">Tanggal lahir</Label>
            <Input
              id="tgl"
              type="date"
              value={tgl}
              onChange={(e) => navigate({ search: { tgl: e.target.value } })}
              className="mt-1 max-w-xs"
            />
          </div>
          {parsed && result && (
            <div className="rounded-lg border border-border bg-card p-4 sm:p-6">
              <p className="text-sm text-muted-foreground">{formatLong(parsed.y, parsed.m, parsed.d)}</p>
              <h2 className="mt-1 text-2xl font-bold">{result.weton}</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Info label="Neptu" value={`${result.neptu}`} />
                <Info label="Neptu Hari + Pasaran" value={`${result.neptuHari} + ${result.neptuPasaran}`} />
                <Info label="Wuku" value={result.wuku} />
                <Info label="Pancasuda" value={result.pancasuda} />
                <Info label="Tanggal Jawa" value={`${result.tanggalJawa} ${result.bulanJawa} ${result.tahunJawa} ${result.windu}`} />
                <Info label="Pranata Mangsa" value={result.pranataMangsa.name} />
              </div>
              <div className="mt-4 rounded-md bg-muted p-3 text-sm">
                <strong>Watak (neptu {result.neptu})</strong>: {WATAK[result.neptu] ?? "-"}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={calendarDatePath(parsed.y, parsed.m, parsed.d) as never}>Lihat detail tanggal lengkap →</Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (typeof window === "undefined") return;
                    const url = window.location.href;
                    const text = `Weton ${formatLong(parsed.y, parsed.m, parsed.d)}: ${result.weton} (neptu ${result.neptu})`;
                    if (navigator.share) {
                      navigator.share({ title: "Cek Weton", text, url }).catch(() => {});
                    } else {
                      navigator.clipboard.writeText(url).then(() => toast.success("Link disalin"));
                    }
                  }}
                >
                  <Share2 className="mr-2 h-3.5 w-3.5" /> Bagikan
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://wa.me/?text=${encodeURIComponent(`Weton ${formatLong(parsed.y, parsed.m, parsed.d)}: ${result.weton} (neptu ${result.neptu}) - ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
                  >
                    <MessageCircle className="mr-2 h-3.5 w-3.5" /> WhatsApp
                  </a>
                </Button>
                <Suspense fallback={null}>
                  <WetonPdfButton initialDate={tgl} />
                </Suspense>
              </div>
            </div>
          )}
        </div>
        <aside><AdSlot size="rectangle" position="sidebar" /></aside>
      </div>
    </SiteShell>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}